<!--
  FormField.vue

  This component displays an input with a label above it.
  Additionally if the prop is passed in, it can set the helper text on the right hand side.
  It supports basic input types and is only styled for basic inputs.

  if it has slot named 'helperText', it will ignore string props 'helperText'.
  eg: 
    <FormField helperText="some text">
      // some html
      <template v-slot:helperText> // note that it must be template
        <p>some text within html element</p>
        <p>second text</p>
      </template>
    </FormField>

    result would be "some text" will not be shown as helper text but two paragraph with "some text within html element" and "second text" will be used as helper text.

  The following events are emitted by this component and handlers can be bound:
  - blur
  - input
-->

<template>
  <div class="form-field" :class="({ slotted: $slots.default }, overrideClass)">
    <div v-if="labelText" class="item1">
      <FormLabel :labelText="labelText" :required="required" :forId="inputId" :class="disabled ? 'disabled' : ''" />
      <label v-if="additionalLabel !== ''" :for="inputId" v-html="additionalLabel" class="additional-label"></label>
    </div>
    <div v-if="$slots.default" class="slotted-items" :class="{ 'with-label': labelText }">
      <slot></slot>
    </div>
    <div v-else class="item2" :class="{ 'no-label': !labelText }">
      <FormInput
        :autocomplete="autocomplete"
        :disabled="disabled"
        :readOnly="readOnly"
        :id="inputId"
        :isDateInput="isDateInput"
        :maxlength="maxlength"
        :name="name"
        :overrideClass="error ? 'error' : null"
        :placeholder="placeholder"
        :prefixLabel="prefixLabel"
        :type="type"
        @blur="handleBlur"
        @keydown="handleKeyDown"
        @input="input"
        v-model="inputValue"
        :overrideValue="overrideValue"
      />
      <label v-if="error" class="error" :for="inputId">
        <svg viewBox="0 0 40 40">
          <use xlink:href="/assets/public/images/icon_error.svg" />
        </svg>
        {{ errorMessage }}
      </label>
    </div>

    <div v-if="helperText || this.$slots.helperText" class="item3 helper-text fr-view" :class="{ 'with-label': labelText, 'no-label': noLabelOverride }">
      <slot v-if="this.$slots.helperText" name="helperText" />
      <p v-else v-html="helperText" />
    </div>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';

import FormInput from './FormInput.vue';
import FormLabel from './FormLabel.vue';

export default {
  name: 'FormField',

  components: {
    FormInput,
    FormLabel,
  },

  props: {
    autocomplete: {
      type: String,
    },

    /**
     * PROPS FOR THIS COMPONENT
     */

    /**
     * The value of the helper text element
     * @type String
     * @values Any string value
     */
    helperText: {
      type: String,
    },

    /**
     * Custom class to set on the parent wrapping div. Can be used to override styles.
     * @type String
     * @values Any string value
     */
    overrideClass: {
      type: String,
    },

    /**
     * Value to override the FormInput component's value. This will force the text input to update to a set override value
     * Only to be used as an escape hatch
     * E.g. 1) Enable toggle PO Box on Member Details, Contact Details to update the address input
     *  2) In some scenarios value of text input isn't cleared when it has v-mask applied on it, this value can be used to clear the value forcefully.(check CLaimsAccount.vue)
     */
    overrideValue: {
      type: String,
    },

    /**
     * Flag to recognise if the input has an error
     * @type Boolean
     * @values none, true, false
     */
    error: {
      type: Boolean,
      default: false,
    },

    /**
     * Message to display when error is set
     * @type String
     * @values Any string value
     */
    errorMessage: {
      type: String,
    },

    /**
     * PROPS FOR THE LABEL CHILD COMPONENT
     */

    /**
     * The value of the actual label text.
     * @type String
     * @values Any string value
     */
    labelText: {
      type: String,
    },

    /**
     * Flag to track if the label should have a required asterisk next to it.
     * @type Boolean
     * @values none, true, false
     * @default true
     */
    required: {
      type: Boolean,
      default: true,
    },

    /**
     * Disabled attribute to set on the input.
     * @type Boolean
     * @values none, true, false
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * ID to set on the input and label's for attribute. If none is provided, a uuid is generated instead.
     * @type String
     * @values Any string value
     */
    forId: {
      type: String,
    },

    /**
     * Flag to detect if input is date type.
     * If true, input masking for date format is enabled.
     * @type Boolean
     * @values none, true, false
     * @default false
     */
    isDateInput: {
      type: Boolean,
      default: false,
    },

    /**
     * Max length of characters for an input field.
     * @type String
     * @values Any String with a number > 0
     */
    maxlength: {
      type: String,
    },

    /**
     * ID to set on the input and label's for attribute. If none is provided, a uuid is generated instead.
     * @type String
     * @values Any string value
     */
    name: {
      type: String,
    },

    /**
     * Placeholder attribute to set on the input.
     * @type String
     * @values Any string value
     */
    placeholder: {
      type: String,
    },

    /**
     * PROPS FOR THE INPUT CHILD COMPONENT
     */

    /**
     * The value of the prefix label. Optional.
     * If set there will be a label the prefixes the input.
     * @type String
     * @values Any string value
     */
    prefixLabel: {
      type: String,
    },

    /**
     * Type to set for the input element.
     * @type String
     * @values Any input tag type values
     * @default "text"
     */
    type: {
      type: String,
      default: 'text',
    },

    /**
     * Value attribute for the input element
     * @type String - default to empty string
     * @values Any string
     */
    modelValue: {
      type: String,
      default: '',
    },

    /**
     * Additional label line.
     * @type String
     * @values Any string value
     */
    additionalLabel: {
      type: String,
      default: '',
    },

    /**
     * If true, render input as read only input
     * @type Boolean
     * @values none, true, false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },

    /**
     * Allow for label override to put support copy in first row
     */
    noLabelOverride: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      inputId: null,
      inputValue: null,
    };
  },

  mounted() {
    // Generate unique UUID if no id prop is passed in
    if (this.forId) {
      this.inputId = this.forId;
    } else {
      this.inputId = uuid();
    }

    // Capture the value attribute
    if (this.modelValue !== undefined && this.modelValue !== null) {
      this.inputValue = this.modelValue;
    }
  },

  computed: {},

  watch: {
    /**
     * Watch the prop to ensure the input value is updated if the parent changes the prop value
     */
    modelValue(val) {
      this.inputValue = val;
    },
  },

  methods: {
    input(newVal) {
      // NOTE: Due to double propagation, the value is already extracted by the time it comes here.
      this.inputValue = newVal.target.value;
      this.$emit('update:modelValue', newVal.target.value);
    },

    /**
     * This will emit the blur event to the parent so that it can be handled in the parent if required.
     */
    handleBlur($e) {
      this.$emit('blur', $e);
    },

    /**
     * This will emit the keydown event to the parent so that it can be handled in the parent if required.
     */
    handleKeyDown($e) {
      this.$emit('keydown', $e);
    },

    /**
     * This will emit the onEnter event to the parent so that it can be handled in the parent if required.
     */
    onEnter() {
      this.$emit('onEnter');
    },
  },
};
</script>

<style lang="scss" scoped>
.form-field {
  @include grid-layout(null, spacer(1));
  @include grid-template(2fr 1fr);
  row-gap: 0;
  margin-bottom: 2rem;

  &.align-checkbox-top {
    :deep(input[type='checkbox'] + label) {
      &::before,
      &::after {
        top: 1.5rem;
      }
    }
  }

  &.inline-block {
    display: inline-block;
    width: 100%;
  }

  .slotted-items {
    @include grid-position(1, 1, null, 2);

    &.with-label {
      @include grid-position(1, 2);
    }
  }

  .item1 {
    @include grid-position(1, 1);
    display: grid;

    label {
      margin-bottom: 0.5rem;
      font-weight: $BOLD;

      &.disabled {
        color: $GREY_600;
      }

      &.additional-label {
        font-weight: unset;
      }
    }
  }

  .item2 {
    @include grid-position(1, 2);

    &.no-label {
      @include grid-position(1, 1);
    }
  }

  .item3 {
    @include grid-position(2, 2);

    &.no-label {
      @include grid-position(2, 1);
    }

    &.helper-text {
      & > :deep(p),
      & > :deep(div) {
        background-color: $WHITE;
        padding: 12px 24px;
        border-left: 3px solid $LIGHT_GREEN;
        border-radius: 2px;
        font-weight: $NORMAL;
        font-size: $CAPTION_FONT_SIZE;
        color: $GREY_800;
        box-shadow: 0 2px 6px rgba($BLACK, 0.06);
      }
    }
  }

  @include mobile {
    @include grid-template(100%);

    .item1 {
      @include grid-position(1, 1);
    }

    .item2 {
      @include grid-position(1, 2);

      &.no-label {
        @include grid-position(1, 1);
      }
    }

    .item3 {
      @include grid-position(1, 3);

      &.no-label {
        @include grid-position(1, 2);
      }
    }

    .slotted-items {
      @include grid-position(1, 2);

      &.no-label {
        @include grid-position(1, 1);
      }
    }

    .helper-text {
      @include grid-position(1, 3);
      margin-top: spacer(1);

      &.no-label {
        @include grid-position(1, 3);
      }
    }
  }

  :deep(label.error) {
    color: $ERROR;
    display: flex;
    align-items: center;
    column-gap: 5px;
    background: $FUNCTIONAL_WARNING_20;
    padding: $BASE;
    margin-top: $BASE;
    border-radius: $XS;
    font-size: font-size(-1);

    svg {
      height: $LG;
      width: $LG;
      fill: $ERROR;
    }
  }

  &.half-width {
    :deep(input) {
      width: calc(50% - 7.5px) !important;
      min-width: rem(200);

      @include mobile {
        width: 100%;
      }
    }
  }
}
</style>
